/**
 * Local image attachments and expense drafts. Images are processed on the device, stored once in
 * IndexedDB and never uploaded. A draft owns its image until the expense is saved; saving moves the
 * image to the expense (see saveExpense with `draftId` in ledger.ts).
 *
 * One image per draft/expense: adding an image replaces the previous one in the same transaction.
 */
import type { AttachmentOrigin } from "@/domain/attachments";
import type { Attachment, DraftForm, ExpenseDraft } from "@/domain/model";
import { newId, sha256Hex } from "@/lib/hash";
import type { ImageProcessor } from "@/integrations/images/process-image";
import { nowIso, type BudgetDatabase } from "./db";

export interface AttachmentOwner {
  kind: "draft" | "expense";
  id: string;
}

export class AttachmentError extends Error {
  override readonly name = "AttachmentError";
}

async function buildAttachment(owner: AttachmentOwner, file: File, origin: AttachmentOrigin, processor: ImageProcessor): Promise<Attachment> {
  // Processing happens before any write: an unsupported file never reaches the database.
  const processed = await processor(file);
  const now = nowIso();
  return {
    id: newId(),
    ownerKind: owner.kind,
    ownerId: owner.id,
    origin,
    mimeType: processed.mimeType,
    data: processed.data,
    byteSize: processed.data.byteLength,
    width: processed.width,
    height: processed.height,
    sha256: await sha256Hex(new Uint8Array(processed.data)),
    processing: processed.processing,
    original: processed.original,
    createdAt: now,
    updatedAt: now,
  };
}

export async function getAttachments(db: BudgetDatabase, owner: AttachmentOwner): Promise<Attachment[]> {
  return db.attachments.where("[ownerKind+ownerId]").equals([owner.kind, owner.id]).sortBy("createdAt");
}

/** Stores an image for a draft or expense, replacing the current one (never two copies). */
export async function setImage(
  db: BudgetDatabase,
  owner: AttachmentOwner,
  file: File,
  origin: AttachmentOrigin,
  processor: ImageProcessor,
): Promise<Attachment> {
  const attachment = await buildAttachment(owner, file, origin, processor);
  await db.transaction("rw", [db.attachments, db.expenseDrafts, db.expenses, db.extractions], async () => {
    if (owner.kind === "draft" && !(await db.expenseDrafts.get(owner.id))) throw new AttachmentError("Bozza non trovata.");
    if (owner.kind === "expense") {
      const expense = await db.expenses.get(owner.id);
      if (!expense) throw new AttachmentError("Spesa non trovata.");
      if (expense.source === "excel_import") throw new AttachmentError("Le righe importate da Excel non hanno immagini.");
    }
    await deleteAttachments(db, await db.attachments.where("[ownerKind+ownerId]").equals([owner.kind, owner.id]).primaryKeys());
    await db.attachments.add(attachment);
    if (owner.kind === "draft") await db.expenseDrafts.update(owner.id, { updatedAt: nowIso() });
  });
  return attachment;
}

/** Deletes images together with what OCR read from them. Call inside a transaction over both. */
async function deleteAttachments(db: BudgetDatabase, ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  await db.extractions.where("attachmentId").anyOf(ids).delete();
  await db.attachments.bulkDelete(ids);
}

export async function removeImage(db: BudgetDatabase, attachmentId: string): Promise<void> {
  await db.transaction("rw", [db.attachments, db.extractions], () => deleteAttachments(db, [attachmentId]));
}

// ─── Drafts ─────────────────────────────────────────────────────────────────────────────────────

export async function createDraft(db: BudgetDatabase, origin: ExpenseDraft["origin"], form: DraftForm | null = null): Promise<ExpenseDraft> {
  const now = nowIso();
  const draft: ExpenseDraft = { id: newId(), origin, form, createdAt: now, updatedAt: now };
  await db.expenseDrafts.add(draft);
  return draft;
}

/**
 * The "Fotografa ricevuta" / "Carica screenshot / foto" entry point: validates and processes the
 * image first, then creates the draft and its attachment together (nothing is stored on failure).
 */
export async function createDraftFromImage(
  db: BudgetDatabase,
  file: File,
  origin: AttachmentOrigin,
  processor: ImageProcessor,
  form: DraftForm | null = null,
): Promise<{ draft: ExpenseDraft; attachment: Attachment }> {
  const now = nowIso();
  const draft: ExpenseDraft = { id: newId(), origin, form, createdAt: now, updatedAt: now };
  const attachment = await buildAttachment({ kind: "draft", id: draft.id }, file, origin, processor);
  await db.transaction("rw", [db.expenseDrafts, db.attachments], async () => {
    await db.expenseDrafts.add(draft);
    await db.attachments.add(attachment);
  });
  return { draft, attachment };
}

export async function updateDraftForm(db: BudgetDatabase, id: string, form: DraftForm): Promise<void> {
  await db.expenseDrafts.update(id, { form, updatedAt: nowIso() });
}

/** Discards a draft and its image (the user chose not to save the expense). */
export async function deleteDraft(db: BudgetDatabase, id: string): Promise<void> {
  await db.transaction("rw", [db.expenseDrafts, db.attachments, db.extractions], async () => {
    await deleteAttachments(db, await db.attachments.where("[ownerKind+ownerId]").equals(["draft", id]).primaryKeys());
    await db.expenseDrafts.delete(id);
  });
}

export interface DraftSummary {
  draft: ExpenseDraft;
  attachments: number;
}

export async function listDrafts(db: BudgetDatabase): Promise<DraftSummary[]> {
  const drafts = await db.expenseDrafts.orderBy("updatedAt").reverse().toArray();
  return Promise.all(drafts.map(async (draft) => ({ draft, attachments: await db.attachments.where("[ownerKind+ownerId]").equals(["draft", draft.id]).count() })));
}
